import { FC, Ref } from 'react';

import styled, { css } from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { BaseCheckIcon } from '@assets/icons';
import { ModalApi } from '@components/Modal';
import Input, { InputContainer } from '@components/Input';

import { TurkTelekomModalSchema } from '../schemas';
import PromotionModal from '../PromotionModal';
import { PromotionsConfigs, Promotion, sharedProps } from './common';
import { Button } from '../common/styled';

const Content = styled.div`
  margin: 40px 0 0;
  font-size: 16px;
  line-height: 1.56;
  color: ${(p) => p.theme.palette.darkGray};
  @media (max-width: ${({ theme }) => theme.breakPoint.LARGE_1X}) {
    display: flex;
    flex-direction: column;
    svg {
      align-self: center;
    }
    row-gap: 20px;
    margin-top: 0px;
  }
`;

const Section = styled.section`
  margin: 32px 0 40px;
  display: flex;
  button {
    height: unset;
    width: 148px;
    font-weight: bold;
  }
  column-gap: 20px;
  ${InputContainer} {
    flex: 1;
  }
  @media (max-width: ${({ theme }) => theme.breakPoint.LARGE_1X}) {
    flex-direction: column;
    row-gap: 20px;
    margin: 0px 0px;
    &:last-child {
      margin-bottom: 20px;
    }
    ${InputContainer} {
      height: 68px;
      flex: unset;
    }
    button {
      height: 68px;
      width: 100%;
    }
  }
`;
const Paragraph = styled.p`
  font-size: 14px;
  line-height: 1.57;
  margin: 0;
  color: ${(p) => p.theme.palette.darkGrey};
  @media (max-width: ${({ theme }) => theme.breakPoint.LARGE_1X}) {
    text-align: center;
  }
`;

const responsiveness = css`
  @media only screen and (max-width: 425px) {
    flex-direction: column;
  }
`;

const Bold = styled.span`
  font-weight: bold;
`;

export interface TurkTelekomModalFormData {
  phoneNumber: string;
  turkTelekomCode: string;
}

const initialValues = {
  phoneNumber: '',
  turkTelekomCode: '',
};

const TTModal: FC<{
  modal?: Ref<ModalApi>;
  isVisible?: boolean;
  isValided?: boolean;
  onCancel?: () => void;
  onSubmit: (formData: TurkTelekomModalFormData) => void;
  promotion?: Promotion;
}> = ({
  modal,
  isVisible,
  isValided = false,
  promotion = 'turktelekom',
  onCancel,
  onSubmit,
  ...rest
}) => {
  const { opening, Logo, logoWidth, promotionCodeLabel, ...promotionProps } =
    PromotionsConfigs[promotion];

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<TurkTelekomModalFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(TurkTelekomModalSchema),
  });

  return (
    <PromotionModal
      modal={modal}
      {...sharedProps}
      {...rest}
      {...promotionProps}
      titleResponsiveness={responsiveness}
      isVisible={isVisible}
      onCancel={onCancel}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Logo width={logoWidth} />
          <Section>
            <Paragraph>
              {opening} <Bold>Bi’ Dünya Fırsat</Bold> şimdi de{' '}
              <Bold>Setur’da</Bold>. Seçili otel ve turlarda geçerli mevcut
              indirimlere ek %6’ya varan indirimden faydalanmak için hemen{' '}
              <Bold>cep telefonu numaranı ve fırsat kodunu girin</Bold>.
            </Paragraph>
          </Section>
          <Section>
            <Controller
              control={control}
              name='phoneNumber'
              render={({ field }) => (
                <Input
                  {...field}
                  label='Cep Telefonu Numarası'
                  hasError={Boolean(errors.phoneNumber)}
                />
              )}
            />
            <Controller
              control={control}
              name='turkTelekomCode'
              render={({ field }) => (
                <Input
                  {...field}
                  label={promotionCodeLabel}
                  hasError={Boolean(errors.turkTelekomCode)}
                />
              )}
            />

            <Button
              disabled={isSubmitting}
              outline
              size='large'
              htmlType='submit'
              isValid={isValided}
            >
              <span>{!isValided ? 'Doğrula' : 'Doğrulandı'}</span>
              <BaseCheckIcon width={24} height={24} />
            </Button>
          </Section>
        </Content>
      </form>
    </PromotionModal>
  );
};

export default TTModal;
